import type {
    ExcludeAccountFields,
    ExcludeStatusFields,
    HubspotMappingField,
} from "app/pages/sales-intelligence/common-components/export-table/types";
import { INTEGRATION_STATUS } from "app/pages/sales-intelligence/pages/management/constants/constants";
import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";
import type { CrmExportLimit, CrmStateInfo, IntegrationStatus } from "../../components/types";
import { CrmFeedLevel, CRMListObjectsType } from "../../types";
import { managementActions } from "../action-creators";
import type { FetchError } from "app/pages/sales-intelligence/types";

// NOTE: BE:IntegrationLeadFilter => FE: Exclusion

export type HubspotIntegrationState = {
    crmConnectionStatus: IntegrationStatus;
    crmStateInfo: CrmStateInfo;
    crmExportLimit: CrmExportLimit | null;
    predefinedOnCreateUpdateClaimEnabled: boolean;
    isCurrentUserVerifiedByCrm: boolean;
    isLoading: boolean;
    toggleLoading: {
        isContactsLoading: boolean;
        isCompaniesLoading: boolean;
    };
    excludeStatusFields: ExcludeStatusFields;
    contactFields: string[];
    isLoadingMappingFields: boolean;
    mappingFields: HubspotMappingField[];
    mappingFieldsError: FetchError | null;
    isLoadingExcludeStatusFields: boolean;
    isExclusionOn: boolean | null;
    exclusionError: string;
    isExclusionLoading: boolean;
    error: string;
    excludeAccountFields: ExcludeAccountFields;
    isLoadingExcludeAccountFields: boolean;
    isUpdatingFilterFields: boolean;
    isUpdatingStatusField: boolean;
};

const HUBSPOT_INTEGRATION_STATE: HubspotIntegrationState = {
    crmConnectionStatus: {
        ownerFirstName: "",
        ownerLastName: "",
        domainsCount: 0,
        syncStatus: INTEGRATION_STATUS.NotIntegrated,
        syncFailedReasonCode: 0,
        lastSynced: "",
        isExportEnabled: false,
        isContactExportEnabled: false,
        isLeadsExportEnabled: false,
        isCompanyAsLeadExportEnabled: false,
        isLeadsFilterEnabled: false,
        isCrmListEnabled: false,
        isCrmLabelsEnabled: false,
        isCrmFeedEnabled: false,
        crmFeedLevel: CrmFeedLevel.User,
        crmListObjectsType: CRMListObjectsType.AccountsAndContacts,
    },
    crmStateInfo: {
        accountExportFieldsCount: 0,
        contactExportFieldsCount: 0,
        leadExportFieldsCount: 0,
    },
    crmExportLimit: null,
    predefinedOnCreateUpdateClaimEnabled: false,
    isCurrentUserVerifiedByCrm: false,
    toggleLoading: {
        isContactsLoading: false,
        isCompaniesLoading: false,
    },
    excludeStatusFields: [],
    contactFields: [],
    isLoadingMappingFields: false,
    mappingFields: [],
    mappingFieldsError: null,
    isLoadingExcludeStatusFields: false,
    isExclusionOn: null,
    isExclusionLoading: false,
    exclusionError: "",
    error: "",
    isLoading: false,
    isLoadingExcludeAccountFields: false,
    excludeAccountFields: [],
    isUpdatingFilterFields: false,
    isUpdatingStatusField: false,
};

export const hubspotIntegrationReducer = createReducer<
    HubspotIntegrationState,
    ActionType<typeof managementActions.hubspot>
>(HUBSPOT_INTEGRATION_STATE)
    .handleAction(managementActions.hubspot.getHubspotStatusAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        crmStateInfo: payload.crmStateInfo,
        crmExportLimit: payload.crmExportLimit ?? null,
        isCurrentUserVerifiedByCrm: payload.isCurrentUserVerifiedByCrm,
        predefinedOnCreateUpdateClaimEnabled: payload.predefinedOnCreateUpdateClaimEnabled,
        isLoading: false,
    }))
    .handleAction(managementActions.hubspot.getHubspotStatusAsync.failure, (state, { payload }) => ({
        ...state,
        error: payload,
        isLoading: false,
    }))
    .handleAction(managementActions.hubspot.getHubspotStatusAsync.request, (state, { payload }) => ({
        ...state,
        isLoading: payload,
    }))
    .handleAction(managementActions.hubspot.getExcludeStatusFieldsAsync.request, (state) => ({
        ...state,
        isLoadingExcludeStatusFields: true,
    }))
    .handleAction(managementActions.hubspot.getExcludeStatusFieldsAsync.success, (state, { payload }) => ({
        ...state,
        excludeStatusFields: payload,
        isLoadingExcludeStatusFields: false,
    }))
    .handleAction(managementActions.hubspot.getHsContactsFieldsAsync.success, (state, { payload }) => ({
        ...state,
        contactFields: payload,
    }))
    .handleAction(managementActions.hubspot.getExcludeStatusFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingExcludeStatusFields: true,
        error: payload,
    }))
    .handleAction(managementActions.hubspot.getExcludeAccountFieldsAsync.request, (state) => ({
        ...state,
        isLoadingExcludeAccountFields: true,
    }))
    .handleAction(managementActions.hubspot.getExcludeAccountFieldsAsync.success, (state, { payload }) => ({
        ...state,
        excludeAccountFields: payload,
        isLoadingExcludeAccountFields: false,
    }))
    .handleAction(managementActions.hubspot.getExcludeAccountFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        isLoadingExcludeAccountFields: false,
        error: payload,
    }))
    .handleAction(managementActions.hubspot.getMappingFieldsAsync.request, (state) => ({
        ...state,
        isLoadingMappingFields: true,
        mappingFieldsError: null,
    }))
    .handleAction(managementActions.hubspot.getMappingFieldsAsync.success, (state, { payload }) => ({
        ...state,
        mappingFields: payload,
        isLoadingMappingFields: false,
    }))
    .handleAction(managementActions.hubspot.getMappingFieldsAsync.failure, (state, { payload }) => ({
        ...state,
        mappingFieldsError: payload,
        isLoadingMappingFields: false,
    }))
    .handleAction(managementActions.hubspot.putFilterFieldsAsync.request, (state) => ({
        ...state,
        isUpdatingFilterFields: true,
    }))
    .handleAction(managementActions.hubspot.putFilterFieldsAsync.success, (state) => ({
        ...state,
        isUpdatingFilterFields: false,
    }))
    .handleAction(managementActions.hubspot.putFilterFieldsAsync.failure, (state) => ({
        ...state,
        isUpdatingFilterFields: false,
    }))
    .handleAction(managementActions.hubspot.putStatusFieldAsync.request, (state) => ({
        ...state,
        isUpdatingStatusField: true,
    }))
    .handleAction(managementActions.hubspot.putStatusFieldAsync.success, (state) => ({
        ...state,
        isUpdatingStatusField: false,
    }))
    .handleAction(managementActions.hubspot.putStatusFieldAsync.failure, (state) => ({
        ...state,
        isUpdatingStatusField: false,
    }))
    .handleAction(managementActions.hubspot.setSyncStatus, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            syncStatus: payload,
        },
    }))
    .handleAction(managementActions.hubspot.disconnectHubspotAsync.request, (state) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            syncStatus: INTEGRATION_STATUS.InProgress,
        },
        isLoading: true,
    }))
    .handleAction(managementActions.hubspot.setIsHsCompaniesExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.hubspot.setIsHsContactsExportEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: {
            ...state.crmConnectionStatus,
            isContactExportEnabled: payload,
        },
    }))
    .handleAction(managementActions.hubspot.disconnectHubspotAsync.success, (state) => ({
        ...state,
        crmConnectionStatus: { ...HUBSPOT_INTEGRATION_STATE.crmConnectionStatus },
        isLoading: false,
    }))
    .handleAction(managementActions.hubspot.disconnectHubspotAsync.failure, (state) => ({
        ...state,
        crmConnectionStatus: { ...HUBSPOT_INTEGRATION_STATE.crmConnectionStatus },
        isLoading: false,
    }))
    .handleAction(managementActions.hubspot.putIntegrationLeadFilterStatusAsync.request, (state) => ({
        ...state,
        isExclusionLoading: true,
    }))
    .handleAction(managementActions.hubspot.putIntegrationLeadFilterStatusAsync.success, (state, { payload }) => ({
        ...state,
        isExclusionOn: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.hubspot.putIntegrationLeadFilterStatusAsync.failure, (state, { payload }) => ({
        ...state,
        exclusionError: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.hubspot.getIntegrationLeadFilterStatusAsync.request, (state) => ({
        ...state,
        isExclusionLoading: true,
    }))
    .handleAction(managementActions.hubspot.getIntegrationLeadFilterStatusAsync.success, (state, { payload }) => ({
        ...state,
        isExclusionOn: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.hubspot.getIntegrationLeadFilterStatusAsync.failure, (state, { payload }) => ({
        ...state,
        exclusionError: payload,
        isExclusionLoading: false,
    }))
    .handleAction(managementActions.hubspot.putContactsToggleAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: false },
    }))
    .handleAction(managementActions.hubspot.putContactsToggleAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: true },
    }))
    .handleAction(managementActions.hubspot.putContactsToggleAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isContactsLoading: false },
    }))
    .handleAction(managementActions.hubspot.putCompaniesStatusAsync.success, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: payload.crmConnectionStatus,
        toggleLoading: { ...state.toggleLoading, isCompaniesLoading: false },
    }))
    .handleAction(managementActions.hubspot.putCompaniesStatusAsync.request, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isCompaniesLoading: true },
    }))
    .handleAction(managementActions.hubspot.putCompaniesStatusAsync.failure, (state) => ({
        ...state,
        toggleLoading: { ...state.toggleLoading, isCompaniesLoading: false },
    }))
    .handleAction(managementActions.hubspot.setIsHsCrmLabelEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, isCrmLabelsEnabled: payload },
    }))
    .handleAction(managementActions.hubspot.setIsHsCrmListEnabled, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, isCrmListEnabled: payload },
    }))
    .handleAction(managementActions.hubspot.setHsCrmListObjectsType, (state, { payload }) => ({
        ...state,
        crmConnectionStatus: { ...state.crmConnectionStatus, crmListObjectsType: payload },
    }));
